import React, { useState } from 'react';
import { Box, Typography, List, ListItem, Theme } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { StringParam, useQueryParam } from 'use-query-params';
import { Skeleton } from '@material-ui/lab';
import { v4 as uuidv4 } from 'uuid';
import { CollapsibleSideBar } from '../../../../common/CollapsibleSideBar';
import { useCampfireQuery } from '../../../../global/network/useCampfireQuery';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { FullscreenDialog } from '../../../../common/dialogs/FullscreenDialog';
import { GET_FORMS } from '../forms.gql';
import { Form } from './Form';
import { FormDetails } from './FormDetails';

const FormListSkeleton = () => {
  return (
    <List disablePadding style={{ marginTop: '12px' }}>
      {Array.from(Array(3)).map(() => (
        <ListItem disableGutters key={uuidv4()}>
          <Skeleton variant='rect' width='100%' height='130px' style={{ borderRadius: '8px' }} />
        </ListItem>
      ))}
    </List>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      alignItems: 'stretch',
      flex: '1 1 0',
      minHeight: 0,
      marginLeft: '10px',
    },
    formContainer: {
      display: 'flex',
      flexDirection: 'column',
      paddingRight: (props: { isMobile: boolean; expanded: boolean }) => (props.isMobile ? '20px' : '60px'),
      overflowY: 'scroll',
      flex: 1,
    },
    title: {
      fontWeight: 800,
      fontSize: '1.25rem',
      color: theme.color.grey.neutralBrand800,
      marginTop: '2rem',
    },
    formList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
      marginTop: 25,
      flex: 1,
      paddingBottom: '2rem',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
    },
    emptyState: {
      textAlign: 'center',
      marginTop: '4rem',
      color: theme.color.grey.neutral400,
    },
  })
);

export function FormsList() {
  const { isMobile, theme } = useCampfireTheme();
  const [formId, setFormId] = useQueryParam('fid', StringParam);
  const [expanded, setExpanded] = useState<boolean>(false);
  const classes = useStyles({ isMobile, expanded });

  const { data, loading, refetch } = useCampfireQuery<any, {}>(GET_FORMS);

  const forms = data?.vm?.forms || [];
  const sortedForms = [...forms].sort((a, b) => a.order - b.order);
  const selectedForm = forms.find((f: any) => f.formId === formId);

  return (
    <div className={classes.container}>
      <Box className={classes.formContainer}>
        {loading ? (
          <FormListSkeleton />
        ) : forms.length === 0 ? (
          <Box className={classes.formList}>
            <Typography style={{ textAlign: 'center', color: theme.color.grey.neutral400, fontSize: '15px' }}>
              No forms yet. Click &quot;Create Form&quot; to get started.
            </Typography>
          </Box>
        ) : (
          <Box className={classes.formList}>
            {sortedForms.map((form) => (
              <Form key={form.formId} form={form} onClick={() => setFormId(form.formId)} />
            ))}
          </Box>
        )}
      </Box>
      {isMobile ? (
        <FullscreenDialog
          fullWidth
          title=''
          fullScreen
          maxWidth={'xs'}
          scroll='paper'
          open={!!formId && !!selectedForm}
          close={() => setFormId(undefined)}
        >
          <Box>{selectedForm ? <FormDetails form={selectedForm} onRefetch={refetch} /> : null}</Box>
        </FullscreenDialog>
      ) : formId && selectedForm ? (
        <CollapsibleSideBar setSiblingExpanded={setExpanded}>
          <Box>
            <FormDetails form={selectedForm} onClose={() => setFormId(undefined)} onRefetch={refetch} />
          </Box>
        </CollapsibleSideBar>
      ) : null}
    </div>
  );
}
